import {
	InspectionTemplate,
	InspectionTemplateSection,
} from "@/shared/models/Inspection";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import UndoIcon from "@mui/icons-material/Undo";
import {
	alpha,
	Box,
	Chip,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import type { ICellRendererParams } from "ag-grid-community";
import { formatRevision, sectionRevisions } from "../utils/templateDrafts";
import { VERSION_STATUS } from "@/pages/configuration/versions/versionStatus";

const statusOf = (template: InspectionTemplate) => {
	if (template.StatusCode === VERSION_STATUS.DRAFT)
		return { key: "draft", label: "Draft", tone: "warning" as const };

	if (template.StatusCode === VERSION_STATUS.SCHEDULED)
		return { key: "scheduled", label: "Scheduled", tone: "info" as const };

	if (template.IsCurrentRev)
		return { key: "current", label: "Current", tone: "success" as const };

	return { key: "superseded", label: "Superseded", tone: "neutral" as const };
};

type PanelProps = ICellRendererParams<InspectionTemplateSection> & {
	revisions: InspectionTemplate[];
	onEdit: (template: InspectionTemplate) => void;
	onView: (template: InspectionTemplate) => void;
	onDuplicate: (template: InspectionTemplate) => void;
	onConfirm: (template: InspectionTemplate) => void;
	onChangeToDraft: (template: InspectionTemplate) => void;
	onDelete: (template: InspectionTemplate) => void;
	onEditPublished: (template: InspectionTemplate) => void;
	canEditPublished: boolean;
};

export const TemplateRevisionsPanel = ({
	data,
	revisions,
	onEdit,
	onView,
	onDuplicate,
	onConfirm,
	onChangeToDraft,
	onDelete,
	onEditPublished,
	canEditPublished,
}: PanelProps) => {
	const section = sectionRevisions(revisions, data?.SectionId ?? null);
	const sectionTemplates = section.all
		.filter((template) => template.IsActive)
		.sort(
			(a, b) =>
				Number(a.IsConfirmed) - Number(b.IsConfirmed) ||
				(b.EffectiveFrom?.getTime() ?? 0) - (a.EffectiveFrom?.getTime() ?? 0) ||
				(b.Rev ?? -1) - (a.Rev ?? -1),
		);

	if (sectionTemplates.length === 0)
		return (
			<Box sx={{ px: 5, py: 1.5 }}>
				<Typography variant="body2" color="text.secondary">
					{`${data?.SectionName ?? "This section"} has no sheet yet. Write its first
					revision from the section row.`}
				</Typography>
			</Box>
		);

	return (
		<Box sx={{ py: 0.5 }}>
			{sectionTemplates.map((template) => {
				const status = statusOf(template);
				const isDraft = template.StatusCode === VERSION_STATUS.DRAFT;
				const isScheduled = template.StatusCode === VERSION_STATUS.SCHEDULED;

				return (
					<Box
						key={template.TemplateId}
						data-test-id={`revision-row-${status.key}`}
						onClick={() => (isDraft ? onEdit(template) : onView(template))}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1.5,
							px: 5,
							py: 0.5,
							borderBottom: "1px solid",
							borderColor: "divider",
							"&:last-of-type": { borderBottom: "none" },
							cursor: "pointer",
							"&:hover": { backgroundColor: "action.hover" },
						}}
					>
						<Typography
							sx={{ fontWeight: 600, minWidth: 90 }}
							variant="caption"
						>
							{formatRevision(template)}
						</Typography>

						<Chip
							size="small"
							label={status.label}
							sx={{
								height: 22,
								fontSize: 12,
								fontWeight: 600,
								border: "none",
								color: (theme) =>
									status.tone === "neutral"
										? theme.palette.text.secondary
										: theme.palette[status.tone].main,
								backgroundColor: (theme) =>
									alpha(
										status.tone === "neutral"
											? theme.palette.text.secondary
											: theme.palette[status.tone].main,
										0.1,
									),
								minWidth: 90,
							}}
						/>

						<Typography variant="body2" color="text.secondary">
							{`${
								template.EffectiveFrom === null
									? "not confirmed yet"
									: `from ${dxtToLocalServerTime(template.EffectiveFrom, "MM/dd/yyyy")}`
							} · ${template.ColumnCount} columns · ${
								template.InspectionCount
							} inspections`}
						</Typography>

						<Box sx={{ flex: 1 }} />

						<Box
							sx={{ display: "flex", alignItems: "center" }}
							onClick={(event) => event.stopPropagation()}
						>
							{isDraft ? (
								<>
									<Tooltip title="Delete Draft">
										<IconButton
											size="small"
											data-test-id="delete-draft-button"
											onClick={() => onDelete(template)}
										>
											<DeleteOutlineIcon fontSize="small" color="error" />
										</IconButton>
									</Tooltip>
									<Tooltip title="Edit Draft">
										<IconButton
											size="small"
											data-test-id="edit-draft-button"
											onClick={() => onEdit(template)}
										>
											<EditOutlinedIcon fontSize="small" color="primary" />
										</IconButton>
									</Tooltip>
									<Tooltip
										title={
											template.ColumnCount === 0
												? "Add at least one column before confirming"
												: "Confirm"
										}
									>
										<span>
											<IconButton
												size="small"
												disabled={template.ColumnCount === 0}
												data-test-id="confirm-draft-button"
												onClick={() => onConfirm(template)}
											>
												<PublishIcon
													fontSize="small"
													color={
														template.ColumnCount === 0 ? "disabled" : "success"
													}
												/>
											</IconButton>
										</span>
									</Tooltip>
								</>
							) : isScheduled ? (
								<Tooltip
									title={
										section.draft !== null
											? "This section already has a draft. Edit or delete it before changing a scheduled revision to Draft."
											: "Back to Draft"
									}
								>
									<span>
										<IconButton
											size="small"
											disabled={section.draft !== null}
											data-test-id="change-to-draft-button"
											onClick={() => onChangeToDraft(template)}
										>
											<UndoIcon fontSize="small" />
										</IconButton>
									</span>
								</Tooltip>
							) : template.IsCurrentRev ? (
								<>
									{template.IsLatest && (
										<Tooltip
											title={
												section.draft !== null
													? "This section already has a draft. Edit or delete it before changing a revision to Draft."
													: template.InspectionCount > 0
														? "An inspection was taken under this revision, so it stays as history"
														: "Back to Draft"
											}
										>
											<span>
												<IconButton
													size="small"
													disabled={
														section.draft !== null ||
														template.InspectionCount > 0
													}
													data-test-id="change-to-draft-button"
													onClick={() => onChangeToDraft(template)}
												>
													<UndoIcon fontSize="small" />
												</IconButton>
											</span>
										</Tooltip>
									)}
									{template.IsLatest && canEditPublished && (
										<Tooltip
											title={
												section.draft !== null
													? "This section already has a draft. Edit or delete it before editing the published revision."
													: "Edit Published"
											}
										>
											<span>
												<IconButton
													size="small"
													disabled={section.draft !== null}
													data-test-id="edit-published-button"
													onClick={() => onEditPublished(template)}
												>
													<EditOutlinedIcon
														fontSize="small"
														color={
															section.draft !== null ? "disabled" : "primary"
														}
													/>
												</IconButton>
											</span>
										</Tooltip>
									)}
									<Tooltip title="Duplicate for another section">
										<IconButton
											size="small"
											data-test-id="duplicate-revision-button"
											onClick={() => onDuplicate(template)}
										>
											<ContentCopyIcon fontSize="small" color="primary" />
										</IconButton>
									</Tooltip>
								</>
							) : (
								<Tooltip title="A newer revision has taken over, so this sheet stays as it was read. Open the row to see what it says.">
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											color: "text.disabled",
											px: 1,
										}}
									>
										<IconButton
											size="small"
											data-test-id="locked-revision-button"
											onClick={() => {}}
										>
											<LockOutlinedIcon fontSize="small" color="disabled" />
										</IconButton>
									</Box>
								</Tooltip>
							)}
						</Box>
					</Box>
				);
			})}
		</Box>
	);
};
